import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCollectibles, deleteCollectible, deleteMultipleCollectibles, getSuggerimentiCategorieProdotto, getSuggerimentiBrands, getSuggerimentiLineeProdotto, getSuggerimentiLicenze, getSuggerimentiPersonaggi, getSuggerimentiArtisti, getSuggerimentiMateriali } from '../services/api';
import { Table, Form, Spinner, Alert, Button, Row, Col, Dropdown, Pagination, Card, InputGroup, OverlayTrigger, Popover, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ActionToolbar from '../common/ActionToolbar';
import MobileListLayout from '../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort, faSortUp, faSortDown, faTimes, faCube, faBoxes, faEuroSign, faPlus, faCog, faSearch, faFileInvoice, faWarehouse, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import ColumnOrderController from '../common/ColumnOrderController';
import BulkEditCollectibleModal from '../common/BulkEditCollectibleModal';
import FilterPanel from '../common/FilterPanel';
import SortableHeader from '../common/SortableHeader';
import StatsCards from '../common/StatsCards';
import { usePageTitle } from '../context/PageTitleContext';
import { getOptimalColumnWidth, getMinColumnWidth, autoFitColumns, autoResizeColumn, resetColumnWidths } from '../common/columnSizingUtils';

const columnHelper = createColumnHelper();

// Colonne numeriche per i filtri avanzati
const numericColumns = ['id', 'prezzo_acquisto', 'prezzo_vendita', 'aliquota_iva', 'quantita', 'fornitore_id'];

// Colonne data per i filtri avanzati
const dateColumns = ['data_uscita'];

const allColumnsMap = {
  id: "ID",
  nome: "Nome",
  descrizione: "Descrizione",
  sku: "SKU",
  barcode: "Barcode",
  prezzo_acquisto: "Prezzo Acquisto",
  prezzo_vendita: "Prezzo Vendita",
  aliquota_iva: "IVA %",
  quantita: "Quantità",
  fornitore_id: "ID Fornitore",
  immagine_url: "URL Immagine",
  pubblica_su_web: "Pubblica su Web",
  in_vetrina: "In Vetrina",
  categoria_prodotto: "Categoria Prodotto",
  brand: "Brand",
  linea_prodotto: "Linea Prodotto",
  licenza: "Licenza",
  personaggio: "Personaggio",
  artista: "Artista",
  materiale: "Materiale",
  dimensioni: "Dimensioni",
  data_uscita: "Data Uscita",
  attributi_specifici: "Attributi Specifici (JSON)",
};

const initialVisibleColumns = ['id', 'nome', 'sku', 'barcode', 'prezzo_vendita', 'quantita',
     'pubblica_su_web', 'in_vetrina']; // Update initialVisibleColumns  

const visibilityKey = 'table-visibility-collectibles';

function CollectibleList() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [collectibles, setCollectibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCollectiblesIds, setSelectedCollectiblesIds] = useState([]);
  const [filterPanelKey, setFilterPanelKey] = useState(0);
  const [distinctValues, setDistinctValues] = useState({});
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-collectibles');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-collectibles');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-collectibles');
    const allColumnIds = Object.keys(allColumnsMap);
    const defaultOrder = ['select', ...allColumnIds];

    if (savedOrder) {
        try {
            let parsedOrder = JSON.parse(savedOrder);
            let validOrder = parsedOrder.filter(id => allColumnIds.includes(id) || id === 'select');
            allColumnIds.forEach(id => {
                if (!validOrder.includes(id)) {
                    validOrder.push(id);
                }
            });
            return validOrder;
        } catch (e) {
            console.error("Errore nel parsing di columnOrder da localStorage", e);
            return defaultOrder;
        }
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-collectibles');
    return saved ? JSON.parse(saved) : {};
  });

  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const savedPage = sessionStorage.getItem('list-currentPage-collectibles');
      return savedPage ? JSON.parse(savedPage) : 1;
    } catch (e) {
      console.error("Errore durante il parsing di currentPage da sessionStorage", e);
      return 1;
    }
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const savedItemsPerPage = sessionStorage.getItem('list-itemsPerPage-collectibles');
      return savedItemsPerPage ? JSON.parse(savedItemsPerPage) : 100;
    } catch (e) {
      console.error("Errore durante il parsing di itemsPerPage da sessionStorage", e);
      return 100;
    }
  });
  const [totalCollectibles, setTotalCollectibles] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sticky columns configuration
  const stickyColumns = ['select'];

  const refetchCollectibles = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const filterFields = useMemo(() => [
    { id: '_global_search', label: 'Cerca', type: 'text', placeholder: 'Titolo, brand, barcode...', colSize: 4 },
    { id: 'categoria_prodotto', label: 'Categoria', type: 'select', options: distinctValues.categoria_prodotto || [], colSize: 2 },
    { id: 'brand', label: 'Brand', type: 'select', options: distinctValues.brand || [], colSize: 2 },
    { id: 'condizione', label: 'Condizione', type: 'select', options: ['nuovo', 'usato_ottime', 'usato_buone', 'usato_discrete', 'danneggiato'], colSize: 2 },
  ], [distinctValues]);

  const handleApplyFilters = useCallback((filterValues) => {
    const newColumnFilters = Object.entries(filterValues)
      .filter(([, v]) => v !== undefined && v !== '' && v !== null)
      .map(([id, value]) => ({ id, value }));
    setColumnFilters(newColumnFilters);
    setCurrentPage(1);
  }, [setColumnFilters, setCurrentPage]);

  const debouncedSetSearchTerm = useMemo(() => () => {}, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const filters = columnFilters.reduce((acc, filter) => {
              const { id, value } = filter;
              
              // Range filter for numbers/dates (array with [min, max])
              if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
                const [min, max] = value;
                
                if (min) {
                  acc[`${id}__gte`] = numericColumns.includes(id) ? parseFloat(min) : min;
                }
                if (max) {
                  acc[`${id}__lte`] = numericColumns.includes(id) ? parseFloat(max) : max;
                }
              }
              // Multi-select filter
              else if (Array.isArray(value)) {
                  if (value.length > 0) {
                      acc[id] = { 'in': value };
                  }
              } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
                  acc[id] = null;
              } else if (value !== undefined && value !== '') {
                  acc[id] = value;
              }
              return acc;
          }, {});

            const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
        const stringifiedFilters = JSON.stringify(filters);

        const response = await getCollectibles((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, stringifiedFilters);
        setCollectibles(response.data.collectibles);
        setTotalCollectibles(response.data.total);
      } catch (err) {
        setError('Errore nel caricamento dei collezionabili.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage, itemsPerPage, sorting, columnFilters, refreshTrigger]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
          const [
              categorieProdottoRes, brandsRes, lineeProdottoRes, licenzeRes, personaggiRes, artistiRes, materialiRes
          ] = await Promise.all([
              getSuggerimentiCategorieProdotto(),
              getSuggerimentiBrands(),
              getSuggerimentiLineeProdotto(),
              getSuggerimentiLicenze(),
              getSuggerimentiPersonaggi(),
              getSuggerimentiArtisti(),
              getSuggerimentiMateriali(),
          ]);
          setDistinctValues({
              categoria_prodotto: categorieProdottoRes.data,
              brand: brandsRes.data,
              linea_prodotto: lineeProdottoRes.data,
              licenza: licenzeRes.data,
              personaggio: personaggiRes.data,
              artista: artistiRes.data,
              materiale: materialiRes.data,
          });
      } catch (error) {
          console.error("Errore nel caricamento dei dati per i filtri:", error);
      }
    };
    fetchFilterData();
  }, []);

  const handleResetFilters = useCallback(() => {
    setColumnFilters([]);
    setCurrentPage(1);
    setSorting([]);
    setFilterPanelKey(k => k + 1);
    sessionStorage.removeItem('list-sorting-collectibles');
    sessionStorage.removeItem('list-filters-collectibles');
    sessionStorage.removeItem('list-currentPage-collectibles');
    sessionStorage.removeItem('list-itemsPerPage-collectibles');
  }, [setColumnFilters, setCurrentPage, setSorting]);

  const getColumnValue = (collectible, columnName) => {
    switch (columnName) {
      case 'data_uscita':
        return collectible[columnName] ? new Date(collectible[columnName]).toLocaleDateString() : 'N/A';
      case 'pubblica_su_web':
        return (
          <Badge bg={collectible[columnName] ? 'success' : 'danger'}>
            {collectible[columnName] ? 'Sì' : 'No'}
          </Badge>
        );
      case 'in_vetrina':
        return (
          <Badge bg={collectible[columnName] ? 'success' : 'danger'}>
            {collectible[columnName] ? 'Sì' : 'No'}
          </Badge>
        );
      case 'attributi_specifici':
        try {
          return JSON.stringify(collectible[columnName]);
        } catch {
          return collectible[columnName] || '[]';
        }
      default:
        return collectible[columnName] != null ? String(collectible[columnName]) : 'N/A';
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={collectibles.length === 0} />,
        cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} />,
        minSize: 30, maxSize: 30, size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: value,
          cell: info => {
            const collectible = info.row.original;
            if (key === 'nome') {
              const imagePopover = (
                <Popover id={`popover-image-${collectible.id}`} style={{ maxWidth: '250px' }}>
                  <Popover.Body>
                    {collectible.immagine_url ? (
                      <img 
                        src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${collectible.immagine_url}`} 
                        alt={collectible.nome} 
                        className="img-fluid" 
                      />
                    ) : (
                      'Nessuna immagine'
                    )}
                  </Popover.Body>
                </Popover>
              );
              return (
                <OverlayTrigger placement="right" overlay={imagePopover} delay={{ show: 250, hide: 400 }}>
                  <span>{getColumnValue(collectible, key)}</span>
                </OverlayTrigger>
              );
            }
            return getColumnValue(collectible, key);
          },
          enableColumnFilter: true,
          enableSorting: true,
          meta: {
            filterVariant: ['pubblica_su_web', 'in_vetrina', 'categoria_prodotto', 'brand', 'linea_prodotto', 'licenza', 'personaggio', 'artista', 'materiale'].includes(key) ? 'select' : 'text',
            filterOptions: 
              ['pubblica_su_web', 'in_vetrina'].includes(key) // Handle boolean filters 
              ? [{value: true, label: 'Sì'}, {value: false, label: 'No'}] 
              : (distinctValues[key] 
                  ? distinctValues[key].map(val => ({ value: val, label: val }))
                  : undefined),
          },
        })
      ),
    ],
    [distinctValues]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: collectibles, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing },
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true, manualSorting: true,
  });

  const handleAutoResize = (column) => {
    autoResizeColumn(column, table);
  };

  const handleAutoFitAllColumns = () => {
    autoFitColumns(table);
  };

  const handleResetColumnWidths = () => {
    resetColumnWidths(table);
  };

  // Calcola statistiche per StatsCards
  const stats = useMemo(() => {
    const totalValue = collectibles.reduce((sum, item) => sum + ((item.prezzo_vendita || 0) * (item.quantita || 0)), 0);
    const skuCount = collectibles.reduce((sum, item) => sum + (item.quantita || 0), 0);
    
    return [
      { label: 'Totale Collectibles', value: totalCollectibles, icon: faCube, color: 'primary' },
      { label: 'SKU in Stock', value: skuCount, icon: faBoxes, color: 'success' },
      { label: 'Valore Magazzino', value: `€ ${totalValue.toFixed(2)}`, icon: faEuroSign, color: 'warning' },
    ];
  }, [collectibles, totalCollectibles]);

  useEffect(() => {
    sessionStorage.setItem('list-sorting-collectibles', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-collectibles', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-currentPage-collectibles', JSON.stringify(currentPage));
  }, [currentPage]);

  useEffect(() => {
    sessionStorage.setItem('list-itemsPerPage-collectibles', JSON.stringify(itemsPerPage));
  }, [itemsPerPage]);

  useEffect(() => {
    localStorage.setItem('table-column-order-collectibles', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-collectibles', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    const newSelectedIds = rows
      .map((row) => row.original.id)
      .filter((id) => id != null);

    setSelectedCollectiblesIds(newSelectedIds);
  }, [rows]);

  // Un clic apre la scheda in visualizzazione, due clic la modifica (stesso meccanismo di FilmList).
  // La selezione delle righe resta sulla casella a sinistra.
  const rowClickTimeoutRef = useRef(null);

  const salvaStatoLista = useCallback(() => {
    sessionStorage.setItem('list-currentPage-collectibles', JSON.stringify(currentPage));
    sessionStorage.setItem('list-itemsPerPage-collectibles', JSON.stringify(itemsPerPage));
    sessionStorage.setItem('list-sorting-collectibles', JSON.stringify(sorting));
    sessionStorage.setItem('list-filters-collectibles', JSON.stringify(columnFilters));
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  const handleRowClick = useCallback((row) => {
    if (rowClickTimeoutRef.current) {
      clearTimeout(rowClickTimeoutRef.current);
      rowClickTimeoutRef.current = null;
      return;
    }
    rowClickTimeoutRef.current = setTimeout(() => {
      salvaStatoLista();
      navigate(`/collectibles/${row.original.id}`);
      rowClickTimeoutRef.current = null;
    }, 250);
  }, [salvaStatoLista, navigate]);

  const sectionByColumn = {
    prezzo_acquisto: 'prezzi',
    prezzo_vendita: 'prezzi',
    aliquota_iva: 'prezzi',
    quantita: 'prezzi',
  };

  const handleRowDoubleClick = (e, row) => {
    const columnId = e.target.closest('td')?.getAttribute('data-column-id');
    const section = sectionByColumn[columnId];
    salvaStatoLista();
    navigate(`/collectibles/edit/${row.original.id}${section ? `#${section}` : ''}`);
  };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalCollectibles / itemsPerPage);
    const paginationItems = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow && totalPages > maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      paginationItems.push(<Pagination.Item key={1} onClick={() => setCurrentPage(1)}>{1}</Pagination.Item>);
      if (startPage > 2) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      paginationItems.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      paginationItems.push(<Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>);
    }

    return paginationItems;
  };

  const deleteApiMap = { collectibles: deleteCollectible };
  const deleteMultipleApiMap = { collectibles: deleteMultipleCollectibles };

  // Usiamo SortableHeader dal componente comune invece di definirlo localmente

  const hasManualFilters = table.getState().columnFilters.length > 0;

  // Conta i filtri attivi per il badge
  const activeFiltersCount = useMemo(() => {
    return table.getState().columnFilters.length;
  }, [table]);

  // Render function per le card mobile
  const renderCollectibleCard = useCallback((row) => {
    const collectible = row.original;
    const isSelected = row.getIsSelected();
    
    return {
      image: collectible.immagine_url || null,
      fallbackIcon: faCube,
      title: collectible.nome,
      subtitle: `ID: ${collectible.id}${collectible.sku ? ` • SKU: ${collectible.sku}` : ''}`,
      badges: (
        <>
          {collectible.brand && (
            <Badge bg="secondary" className="me-1" style={{ fontSize: '0.65rem' }}>
              {collectible.brand}
            </Badge>
          )}
          {collectible.categoria_prodotto && (
            <Badge bg="info" className="me-1" style={{ fontSize: '0.65rem' }}>
              {collectible.categoria_prodotto}
            </Badge>
          )}
          {collectible.quantita !== undefined && (
            <Badge bg={collectible.quantita > 0 ? 'success' : 'danger'} style={{ fontSize: '0.65rem' }}>
              <FontAwesomeIcon icon={faBoxes} className="me-1" />
              Qta: {collectible.quantita}
            </Badge>
          )}
          {collectible.prezzo_vendita && (
            <Badge bg="primary" style={{ fontSize: '0.65rem' }}>
              <FontAwesomeIcon icon={faEuroSign} className="me-1" />
              €{collectible.prezzo_vendita}
            </Badge>
          )}
          {collectible.pubblica_su_web && (
            <Badge bg="success" style={{ fontSize: '0.65rem' }}>
              Web
            </Badge>
          )}
          {collectible.in_vetrina && (
            <Badge bg="warning" style={{ fontSize: '0.65rem' }}>
              Vetrina
            </Badge>
          )}
        </>
      ),
      onClick: () => navigate(`/collectibles/edit/${collectible.id}`)
    };
  }, [navigate]);

  // Quick filters per mobile
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faCube,
      isActive: true,
      onClick: () => {},
      gradients: ['#6c757d', '#495057']
    }
  ], []);

  // Actions dropdown content (vuoto per CollectibleList, solo il pulsante di creazione)
  const actionsDropdownContent = null;

  usePageTitle('Collezionabili');

  return (
    <>
      <StatsCards stats={stats} />

    <Card>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          <MobileListLayout
            entityName="Collezionabile"
            entityNamePlural="Collezionabili"
            createPath="/collectibles/new"
            createButtonText="Nuovo Collezionabile"
            quickFilters={quickFilters}
            actionsDropdownContent={actionsDropdownContent}
            renderCard={renderCollectibleCard}
            selectedIds={selectedCollectiblesIds}
            onToggleSelect={(collectibleId) => {
              const row = table.getRowModel().rows.find(r => r.original.id === collectibleId);
              if (row) row.toggleSelected();
            }}
            onCardClick={(collectible) => navigate(`/collectibles/edit/${collectible.id}`)}
            onDeleteMultiple={() => {
              if (window.confirm(`Vuoi eliminare ${selectedCollectiblesIds.length} collezionabile/i selezionato/i?`)) {
                deleteMultipleApiMap.collectibles(selectedCollectiblesIds).then(() => {
                  refetchCollectibles();
                  setSelectedCollectiblesIds([]);
                  if (table) table.toggleAllRowsSelected(false);
                });
              }
            }}
            extraStickyActions={
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowBulkEditModal(true)}
                className="shadow-sm"
              >
                <FontAwesomeIcon icon={faCog} className="me-1" />
                Modifica
              </Button>
            }
            table={table}
            onSearchChange={(e) => {
              const val = e.target.value;
              setColumnFilters(prev => {
                const without = prev.filter(f => f.id !== '_global_search');
                return val ? [...without, { id: '_global_search', value: val }] : without;
              });
              setCurrentPage(1);
            }}
            searchValue={columnFilters.find(f => f.id === '_global_search')?.value || ''}
            onResetFilters={handleResetFilters}
            hasFilters={hasManualFilters}
            activeFiltersCount={activeFiltersCount}
          />
        ) : (
          // Desktop: Layout originale
          <Row className="align-items-center">
          <Col md={6} className="d-flex justify-content-start align-items-center">
            <Button as={Link} to="/collectibles/new" className="me-2" size="sm" variant="success">Nuovo Collezionabile</Button>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="secondary" id="dropdown-actions" size="sm">Azioni</Dropdown.Toggle>
              <Dropdown.Menu>
                {/* Azioni stampa rimosse per ora */}
              </Dropdown.Menu>
            </Dropdown>

          </Col>
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra Colonne</h6>
                    <div className="d-flex flex-column">
                      {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="outline-secondary"
              size="sm"
              className="ms-2"
              onClick={handleAutoFitAllColumns}
              title="Adatta tutte le colonne al contenuto"
            >
              Auto-Fit
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="ms-2"
              onClick={handleResetColumnWidths}
              title="Ripristina larghezze predefinite"
            >
              Reset Colonne
            </Button>
            <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters}>Azzera Filtri</Button>
          </Col>
        </Row>
        )}
      </Card.Header>
      <Card.Body>
        <FilterPanel key={filterPanelKey} fields={filterFields} onApply={handleApplyFilters} />
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && !isMobile && (
            // Vista Desktop con Tabella
            <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
              <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
              <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <React.Fragment key={headerGroup.id}>
                      <tr>
                        {headerGroup.headers.map(header => (
                          <SortableHeader
                            key={header.id}
                            header={header}
                            table={table}
                            sorting={sorting}
                            setSorting={setSorting}
                            handleAutoResize={handleAutoResize}
                            stickyColumns={stickyColumns}
                          />
                        ))}
                      </tr>

                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} onClick={() => handleRowClick(row)} onDoubleClick={(e) => handleRowDoubleClick(e, row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} data-column-id={cell.column.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        
        {/* Action Toolbar - Desktop version */}
        {!isMobile && (
          <ActionToolbar selectedItemIds={selectedCollectiblesIds} entityType="collectibles" onDeleteSuccess={refetchCollectibles} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleApiMap} onBulkEdit={() => setShowBulkEditModal(true)} />
        )}
      </Card.Body>
      {!isMobile && (
        <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
          <Col xs="auto">
            <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalCollectibles / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalCollectibles / itemsPerPage))} disabled={currentPage === Math.ceil(totalCollectibles / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
      )}
      {/* Bulk Edit Modal for Collectibles */}
      <BulkEditCollectibleModal
      show={showBulkEditModal}
      onHide={() => setShowBulkEditModal(false)}
      selectedIds={selectedCollectiblesIds}
      onSuccess={() => {
        refetchCollectibles();
        table.toggleAllRowsSelected(false);
        setShowBulkEditModal(false);
      }}
    /> 
    </Card>
  </>
  );
}

export default CollectibleList;
